import {Input, ReplaceInputAction} from "../state/input";
import {put, join, call, spawn, race, delay, select} from "redux-saga/effects";
import {StatusAction, StatusRequiredAction} from "../state/status";
import {DetectMedia, LoadMedias, ProcessMedias} from "./media";
import {LoadAdvertisement, ProcessAdvertisement} from "./advertisement";
import {Ping} from "./ping";
import {Task} from "redux-saga";
import {Advertisement as AdvertisementOutput} from "../state/source";
import {Media} from "../state/types";
import {ApiClient} from "../../../generated/ApiClient";
import {FrontWebAdvertisementsModel, FrontWebUserLikedModel} from "../../../generated";
import {AppState} from "../state";
import {PositionAction} from "../state/position";
import {PositionsAction} from "../state/positions";
import {SpeedAction} from "../state/speed";
import {QualityType} from "../state/qualities";
import {slugifyParse} from "./slugifyParse";
import {slugifyPath} from "../../../utils/slugifyPath";
import {generateUUID} from "./utils";
import {IsMobile} from "./utils";


export function* source(input: Input) {
    const uuid = generateUUID();
    yield put<StatusAction>({
        type: "Status",
        payload: "LOADING"
    });

    const pingPromise: Task = yield spawn(Ping)
    let inputPosition: number | undefined = undefined;
    let inputDuration: number | undefined = undefined
    let slug: string | undefined;


    const qualities: QualityType[] = yield select((appState: AppState) => appState.qualities);

    const parsed = slugifyParse(input.slug);

    const resolver = async () => {
        slug = slugifyPath(parsed, true);
        const advertisements = await (async () => {
            const resolved = "radioSlug" in parsed ? (async() => {
                try {
                    if ("id" in parsed) {
                        return await new ApiClient().getApiRadioAdvertisementsEpisode({
                            slug: parsed.radioSlug,
                            episodeId: `${parsed.id}`
                        });
                    }
                    return await new ApiClient().getApiRadioAdvertisements({
                        slug: parsed.radioSlug
                    });
                }
                catch (e)
                {
                    return {
                        tritonId: undefined,
                        advertisements: []
                    } as FrontWebAdvertisementsModel;
                }
            })() : Promise.resolve({
                tritonId: undefined,
                advertisements: []
            } as FrontWebAdvertisementsModel);

            return resolved;

        })();

        let episodeAdvertisements: FrontWebAdvertisementsModel | null = null;

        const streams = (async () => {
            if ("id" in parsed) {
                const data = await (new ApiClient().getApiPodcastEpisode({
                    programSlug: parsed.programSlug,
                    id: parsed.id
                }));


                inputDuration = data?.duration;

                if (data.advertisements?.advertisements.length)
                    episodeAdvertisements = data.advertisements;

                return [data.mediaUrl];
            } else {
                const defaulted = await new ApiClient().getApiStreamPlaylistByDefault2({
                    slug: input.slug!
                });

                const founded = qualities.find(x => x.slug === parsed.radioSlug);
                if (founded) {
                    try {
                        const data = await fetch(founded.url, {
                            credentials: "include"
                        }).then(x => x.json());
                        return data as string[];
                    } catch (e) {
                    }
                }
                return defaulted;
            }
        })();

        return Promise.all([advertisements, streams]).then(x => {
            if (episodeAdvertisements && !advertisements?.advertisements.length) {
                return [
                    episodeAdvertisements,
                    x[1]
                ]
            }
            return x;
        }).catch(e => {
            console.error(e);
            return false;
        });
    }


    new ApiClient().postApiStatisticsPlayer({
        frontWebStatisticsPlayerModel: {
            uuid,
            isMobile: IsMobile(),
            state: "load",
        }
    }, {
        credentials: "omit"
    })

    const urlData: {
        timeout: boolean,
        data: [advertisemenst: FrontWebAdvertisementsModel, streams: string[]] | false
    } = yield race({
        timeout: delay(10000),
        data: call(() => resolver())
    });


    if (urlData.timeout) {
        new ApiClient().postApiStatisticsPlayer({
            frontWebStatisticsPlayerModel: {
                uuid,
                isMobile: IsMobile(),
                state: "timeout",
            }
        }, {
            credentials: "omit"
        })
    } else if (urlData.data) {
        new ApiClient().postApiStatisticsPlayer({
            frontWebStatisticsPlayerModel: {
                uuid,
                isMobile: IsMobile(),
                state: urlData.data[0] && urlData.data[0].advertisements.length ? "with_advertisement" : "no_advertisement",
            }
        }, {
            credentials: "omit"
        })
    }

    if (urlData.timeout || !urlData.data) {
        yield put<StatusRequiredAction>({
            type: "RequiredStatus",
            payload: "STOP"
        });
        return;
    } else {
        input = {
            slug: input.slug,
            triton: urlData.data[0].tritonId,
            advertisements: urlData.data[0].advertisements,
            streams: urlData.data[1],
        }
    }

    const advSlug = "programSlug" in parsed ? parsed.programSlug : parsed.radioSlug

    const advPromise: Task = yield spawn(LoadAdvertisement, input.advertisements, advSlug, uuid);

    const streamsPromise: Task = Array.isArray(input.streams) ? yield spawn(DetectMedia, input.streams) : yield spawn(LoadMedias, input.streams);

    const pinged: boolean = yield join(pingPromise);

    if (pinged) {

        yield put<ReplaceInputAction>({
            type: "ReplaceInput",
            payload: input
        })

        const advertisementForPlay: AdvertisementOutput[] = yield join(advPromise);
        if (advertisementForPlay && advertisementForPlay.length) {
            new ApiClient().postApiStatisticsPlayer({
                frontWebStatisticsPlayerModel: {
                    uuid,
                    isMobile: IsMobile(),
                    state: "found_advertisement",
                }
            }, {
                credentials: "omit"
            })
        } else {
            new ApiClient().postApiStatisticsPlayer({
                frontWebStatisticsPlayerModel: {
                    uuid,
                    isMobile: IsMobile(),
                    state: "notfound_advertisement",
                }
            }, {
                credentials: "omit"
            })
        }

        if (inputDuration) {

            let position: number = (yield select((state: AppState) => state.positions.find(x => x.slug === slug)?.time)) ?? inputPosition ?? 0;
            const hasPosition: FrontWebUserLikedModel[] = yield call(async () => {
                if (slug) {
                    const parsed = slugifyParse(slug);
                    if ("id" in parsed)
                        return await new ApiClient().getApiUserIsLikedEpisodes({
                            episodes: [`${parsed.id}`]
                        }, {
                            credentials: "include"
                        }).catch(() => null);
                    return null;
                }
            });

            if (hasPosition && hasPosition.length)
                position = hasPosition[0].listenedTime;

            if (inputDuration - 3 < position) {
                position = 0;
            }

            yield put<PositionAction>({
                type: "Position",
                payload: position
            });

            yield put<PositionsAction>({
                type: "Positions",
                payload: {
                    slug: slug!,
                    time: position,
                    save: true
                }
            });
        } else {
            yield put<SpeedAction>({
                type: "Speed",
                payload: 1
            })
            yield put<PositionAction>({
                type: "Position",
                payload: 0
            });
        }

        if (advertisementForPlay) {
            for (let advPos = 0; advPos < advertisementForPlay.length; advPos++) {
                yield call(ProcessAdvertisement, advertisementForPlay[advPos], advSlug, uuid);
            }
        }

        const content: Media[] = yield join(streamsPromise);
        if (content.length > 0) {
            new ApiClient().postApiStatisticsPlayer({
                frontWebStatisticsPlayerModel: {
                    uuid,
                    isMobile: IsMobile(),
                    state: "begin_source",
                }
            }, {
                credentials: "omit"
            })
            yield call(ProcessMedias, content, uuid);
        }
    } else {
        new ApiClient().postApiStatisticsPlayer({
            frontWebStatisticsPlayerModel: {
                uuid,
                isMobile: IsMobile(),
                state: "no_ping",
            }
        }, {
            credentials: "omit"
        })
    }

    yield put<StatusRequiredAction>({
        type: "RequiredStatus",
        payload: "STOP"
    })
}